import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Dropdown,
  Flex,
  Input,
  Table,
  Tag,
  theme,
  Typography,
  type TableColumnsType,
} from "antd";
import { MoreOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { AttributesIcon } from "@/components/icon";
import { PaginationTotal } from "@/components/pagination-total";
import { axiosInstance } from "@/utilities";
import {
  API_URL_PIM_ATTRIBUTES,
  API_URL_PIM_ATTRIBUTE_SETS,
} from "@/services/XCONS_ENV";
import type { AttributeRow, AttributeSetRow } from "../types";
import { AttributeDrawer } from "./AttributeDrawer";
import {
  AttributeFiltersButton,
  type AttributeFiltersState,
} from "./AttributeFilters";
import { FILAS_POR_PAGINA, OPCIONES_POR_PAGINA } from "@/config/paginacion";

const { Text, Title } = Typography;

const TIPOS = [
  "text",
  "textarea",
  "select",
  "multiselect",
  "boolean",
  "price",
  "date",
];

/**
 * Listado de atributos del catálogo (PIM Fase 16).
 *
 * **El contador dice "valores" y no "productos", a propósito.** Son filas de
 * `catalog_product_entity_*`, y un producto puede tener varias del mismo atributo —una por
 * store view—: `visibility` tiene 236.265 filas para 50.327 productos. Es lo único que se
 * puede contar rápido sobre 2,3 M de filas (83 ms contra 2,9 s), y para lo que el listado
 * tiene que contestar —qué está muerto, qué tan grande es— alcanza. El número exacto de
 * productos lo da el aviso de impacto del form, que es por atributo y va justo antes de
 * decidir.
 */
export const CatalogAttributesList: React.FC = () => {
  const navigate = useNavigate();
  const { token } = theme.useToken();

  const [rows, setRows] = useState<AttributeRow[]>([]);
  const [sets, setSets] = useState<AttributeSetRow[]>([]);
  const [grupos, setGrupos] = useState<Array<{ name: string; setCount: number }>>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [filtros, setFiltros] = useState<AttributeFiltersState>({
    soloFacetas: false,
  });
  const { tipo, conjunto, grupo, soloFacetas } = filtros;

  const [editando, setEditando] = useState<string | null | undefined>(
    undefined,
  );

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosInstance.get<AttributeRow[]>(
        API_URL_PIM_ATTRIBUTES,
        {
          params: {
            search: search || undefined,
            frontendInput: tipo,
            filterable: soloFacetas ? "true" : undefined,
            attributeSetId: conjunto,
            groupName: grupo,
          },
        },
      );
      setRows(data);
    } catch (e) {
      setError(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudieron cargar los atributos",
      );
    } finally {
      setLoading(false);
    }
  }, [search, tipo, soloFacetas, conjunto, grupo]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  useEffect(() => {
    axiosInstance
      .get<AttributeSetRow[]>(API_URL_PIM_ATTRIBUTE_SETS)
      .then(({ data }) => setSets(data))
      .catch(() => undefined);
    axiosInstance
      .get<Array<{ name: string; setCount: number }>>(
        `${API_URL_PIM_ATTRIBUTES}/groups`,
      )
      .then(({ data }) => setGrupos(data))
      .catch(() => undefined);
  }, []);

  const columnas: TableColumnsType<AttributeRow> = useMemo(
    () => [
      {
        title: "Código",
        dataIndex: "code",
        key: "code",
        width: "30%",
        sorter: (a, b) => a.code.localeCompare(b.code),
        render: (code: string) => <Text strong>{code}</Text>,
      },
      { title: "Etiqueta", dataIndex: "label", key: "label", width: "35%" },
      {
        title: "Tipo",
        dataIndex: "frontendInput",
        key: "frontendInput",
        width: "20%",
        render: (t: string | null) => <Tag>{t ?? "—"}</Tag>,
      },
      {
        title: "Valores",
        dataIndex: "valueCount",
        key: "valueCount",
        width: "15%",
        align: "right" as const,
        sorter: (a, b) => a.valueCount - b.valueCount,
        render: (n: number, row) =>
          row.backendType === "static" ? (
            <Text type="secondary">—</Text>
          ) : n === 0 ? (
            // Cero valores sobre 45.300 productos es la señal de un atributo muerto: se
            // marca, porque es medio punto del listado.
            <Tag color="default">sin uso</Tag>
          ) : (
            n.toLocaleString("es-AR")
          ),
      },
    ],
    [],
  );

  return (
    <>
      {/* Mismo encabezado que el listado de Catálogo: el ⋮ cambia de vista, y Conjuntos
        no está ahí sino en su botón, porque es una subsección de Atributos
        (xcons-docs#390). */}
      <Flex align="center" gap={8} style={{ marginBottom: 24 }}>
        <AttributesIcon style={{ fontSize: 20 }} />
        <Title level={3} style={{ marginBottom: 0 }}>
          Atributos
        </Title>
        <Dropdown
          trigger={["click"]}
          menu={{
            selectedKeys: ["attributes"],
            items: [
              { key: "catalog", label: "Catálogo" },
              { key: "attributes", label: "Atributos" },
            ],
            onClick: ({ key }) => {
              if (key === "catalog") navigate("/catalog");
            },
          }}
        >
          <Button icon={<MoreOutlined />} />
        </Dropdown>
      </Flex>
      {/* Sin Card, igual que Productos: la tabla llega sola a los bordes y la barra de
        acciones va en su `title` (xcons-docs#390). */}
      <Table<AttributeRow>
        rowKey="code"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columnas}
        // Alto fijo con scroll vertical, el mismo que Presupuestos, Pedidos y Contactos.
        // Sin `x: "max-content"`: acá las columnas entran en el ancho de la pantalla, con
        // anchos en porcentaje para que se repartan igual en cualquier tamaño, así que la
        // tabla se ve completa sin scroll horizontal, como en Catálogo (xcons-docs#390,
        // xcons-docs#408).
        scroll={{ y: 55 * 10.5 }}
        // Filas compactas, paginador de tamaño normal: `size="small"` de la tabla
        // achica también el paginador, y tiene que ser el mismo que en el resto de las
        // tablas (xcons-docs#390).
        pagination={{
          size: "default",
          defaultPageSize: FILAS_POR_PAGINA,
          showSizeChanger: true,
          pageSizeOptions: OPCIONES_POR_PAGINA,
          showTotal: (t) => (
            <PaginationTotal total={t} entityName="atributos" />
          ),
        }}
        onRow={(row) => ({
          style: { cursor: "pointer" },
          onClick: () => setEditando(row.code),
        })}
        title={() => (
          <Flex vertical gap={16}>
            {/* Una sola línea, como la de Productos: buscador, Filtros, Conjuntos y
            Nuevo atributo (xcons-docs#390). */}
            <Flex gap={8}>
              <Input.Search
                allowClear
                style={{ flex: 1 }}
                placeholder="Buscar por código o etiqueta"
                defaultValue={search}
                onSearch={setSearch}
              />
              <AttributeFiltersButton
                filters={filtros}
                applyFilters={setFiltros}
                tipos={TIPOS}
                sets={sets}
                grupos={grupos}
              />
              <Button onClick={() => navigate("/catalog/attribute-sets")}>
                Conjuntos
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setEditando(null)}
              >
                Nuevo atributo
              </Button>
            </Flex>

            {/* Debajo de la búsqueda, como en Productos. La búsqueda y los filtros los
            resuelve el servidor, así que con alguno activo ya es la cuenta filtrada, y
            sin ninguno, el total (xcons-docs#390). */}
            <Text style={{ color: token.colorTextSecondary }}>
              {rows.length} atributos
            </Text>

            {error && <Alert type="error" showIcon message={error} />}
          </Flex>
        )}
      />

      <AttributeDrawer
        open={editando !== undefined}
        code={editando ?? null}
        onClose={() => setEditando(undefined)}
        onSaved={() => void cargar()}
      />
    </>
  );
};
